import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { listHoldReasons, type HoldReason } from "service/business";
import { cancelButton, primaryButton } from "./styles";

type Props = {
  /** The item being sent back; null keeps the dialog closed. */
  itemLabel: string | null;
  busy: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: (reasonCode: string, reasonNote: string) => void;
};

export function SendBackDialog({ itemLabel, busy, error, onCancel, onConfirm }: Readonly<Props>) {
  const [reasons, setReasons] = useState<HoldReason[]>([]);
  const [code, setCode] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    listHoldReasons().then(setReasons).catch(() => setReasons([]));
  }, []);
  useEffect(() => {
    if (itemLabel === null) {
      setCode("");
      setDetail("");
    }
  }, [itemLabel]);

  const reason = reasons.find((r) => r.code === code);
  const needsDetail = Boolean(reason?.needsDetail);

  return (
    <Dialog open={itemLabel !== null} onOpenChange={(open) => !open && !busy && onCancel()}>
      <DialogContent className="max-w-lg p-0 gap-0 bg-white-A700 rounded-lg">
        <DialogHeader className="px-8 pt-8 pb-4 text-left">
          <DialogTitle className="text-xl font-bold text-black-label">
            Send back: {itemLabel}
          </DialogTitle>
        </DialogHeader>
        <div className="px-8 pb-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" id="send-back-reason">
              Reason <span aria-hidden>*</span>
            </span>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger aria-labelledby="send-back-reason" className="cursor-pointer">
                <SelectValue placeholder="Pick a reason" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.code} value={r.code} className="cursor-pointer">
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-[#6B7280]">Shown to the business.</span>
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">
              {needsDetail ? <>Add detail <span aria-hidden>*</span></> : "Add detail (optional)"}
            </span>
            <Textarea
              // Tailwind's ring-* draws nothing on a field here (see .field-focus-ring in index.css).
              className="border-gray-500 field-focus-ring"
              aria-required={needsDetail}
              value={detail}
              maxLength={1000}
              onChange={(e) => setDetail(e.target.value)}
            />
          </label>
          {error && (
            <p className="text-sm text-red-600" role="alert">{error}</p>
          )}
        </div>
        <DialogFooter className="px-8 py-5 flex-row justify-end gap-2.5 rounded-b-lg bg-gray-button-gray-lite">
          <button type="button" className={cancelButton} disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryButton}
            disabled={!reason || busy || (needsDetail && !detail.trim())}
            onClick={() => reason && onConfirm(reason.code, detail.trim() || reason.label)}
          >
            {busy ? "Sending back…" : "Send back"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
